import { useQuery } from '@tanstack/react-query'
import { useState } from 'react'
import api from '@/lib/api'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/Card'
import { formatCurrency } from '@/lib/utils'
import { Target, Loader2, Info } from 'lucide-react'

interface SalesGoalRow {
  catalog_item_id: string
  code: string
  name: string
  category: string | null
  year: number
  goal_brl: number
  realized_brl: number
  pct_achievement: number
  bucket: 'green' | 'yellow' | 'red'
}

interface SalesGoalsResponse {
  year: number
  totals: { goal_brl: number; realized_brl: number; pct: number }
  products: SalesGoalRow[]
}

function useSalesGoals(year?: number) {
  return useQuery<SalesGoalsResponse>({
    queryKey: ['sales-goals', year],
    queryFn: async () => {
      const res = await api.get('/v1/catalog/insights/sales-goals', {
        params: year ? { year } : undefined,
      })
      return res.data
    },
  })
}

const bucketColor: Record<string, { bar: string; tag: string; ring: string }> = {
  green:  { bar: 'bg-green-500',  tag: 'bg-green-500/15 text-green-600',   ring: 'ring-green-500/30' },
  yellow: { bar: 'bg-amber-500',  tag: 'bg-amber-500/15 text-amber-600',   ring: 'ring-amber-500/30' },
  red:    { bar: 'bg-red-500',    tag: 'bg-red-500/15 text-red-600',       ring: 'ring-red-500/30' },
}

export function SalesGoalsSection() {
  const currentYear = new Date().getFullYear()
  const [year, setYear] = useState<number>(currentYear)
  const { data, isLoading } = useSalesGoals(year)

  if (isLoading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Target className="h-5 w-5" /> Metas anuais por produto
          </CardTitle>
        </CardHeader>
        <CardContent className="flex justify-center py-8">
          <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
        </CardContent>
      </Card>
    )
  }

  if (!data || !data.products?.length) {
    return (
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between flex-wrap gap-2">
            <CardTitle className="flex items-center gap-2">
              <Target className="h-5 w-5" /> Metas anuais por produto
            </CardTitle>
            <select
              className="px-2 py-1 rounded-md border bg-background text-sm"
              value={year}
              onChange={(e) => setYear(parseInt(e.target.value, 10))}
            >
              {[currentYear - 1, currentYear, currentYear + 1].map((y) => (
                <option key={y} value={y}>{y}</option>
              ))}
            </select>
          </div>
        </CardHeader>
        <CardContent>
          <div className="flex items-start gap-2 text-sm text-muted-foreground p-4 rounded-lg bg-muted/30">
            <Info className="h-4 w-4 mt-0.5 flex-shrink-0" />
            <span>
              Nenhum produto com meta cadastrada para <strong>{year}</strong>. Cadastre uma meta anual em
              <strong> Catálogo &gt; produto &gt; Controles comerciais</strong> (somente Gestor Comercial, CFO ou CEO).
            </span>
          </div>
        </CardContent>
      </Card>
    )
  }

  const overall = data.totals
  const overallBucket: 'green' | 'yellow' | 'red' =
    overall.pct >= 100 ? 'green' : overall.pct >= 70 ? 'yellow' : 'red'

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between flex-wrap gap-2">
          <CardTitle className="flex items-center gap-2">
            <Target className="h-5 w-5" /> Metas anuais por produto
            <span className="text-xs font-normal text-muted-foreground ml-1">
              (vendido vs meta · propostas aceitas/assinadas no ano)
            </span>
          </CardTitle>
          <select
            className="px-2 py-1 rounded-md border bg-background text-sm"
            value={year}
            onChange={(e) => setYear(parseInt(e.target.value, 10))}
          >
            {[currentYear - 1, currentYear, currentYear + 1].map((y) => (
              <option key={y} value={y}>{y}</option>
            ))}
          </select>
        </div>
      </CardHeader>
      <CardContent>
        {/* Totalizador geral */}
        <div className={`mb-4 p-3 rounded-lg ring-1 ${bucketColor[overallBucket].ring} bg-muted/20`}>
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm font-medium">Total {year}</span>
            <span className={`text-sm font-bold px-2 py-0.5 rounded ${bucketColor[overallBucket].tag}`}>
              {overall.pct.toFixed(1)}%
            </span>
          </div>
          <div className="text-xs text-muted-foreground mb-2">
            {formatCurrency(overall.realized_brl)} de {formatCurrency(overall.goal_brl)}
          </div>
          <div className="h-2 rounded-full bg-muted overflow-hidden">
            <div
              className={`h-full ${bucketColor[overallBucket].bar} transition-all`}
              style={{ width: `${Math.min(overall.pct, 100)}%` }}
            />
          </div>
        </div>

        {/* Lista por produto */}
        <div className="space-y-2">
          {data.products.map((p) => {
            const cols = bucketColor[p.bucket]
            return (
              <div key={p.catalog_item_id} className="p-2 rounded border bg-background hover:bg-muted/30 transition">
                <div className="flex items-center justify-between gap-2 mb-1.5">
                  <div className="min-w-0">
                    <div className="text-sm font-medium truncate">{p.name}</div>
                    <div className="text-xs text-muted-foreground">
                      {p.code}{p.category ? ` · ${p.category}` : ''}
                    </div>
                  </div>
                  <span className={`text-xs font-bold px-2 py-0.5 rounded flex-shrink-0 ${cols.tag}`}>
                    {p.pct_achievement.toFixed(1)}%
                  </span>
                </div>
                <div className="text-xs text-muted-foreground mb-1">
                  {formatCurrency(p.realized_brl)} / {formatCurrency(p.goal_brl)}
                </div>
                <div className="h-1.5 rounded-full bg-muted overflow-hidden">
                  <div
                    className={`h-full ${cols.bar} transition-all`}
                    style={{ width: `${Math.min(p.pct_achievement, 100)}%` }}
                  />
                </div>
              </div>
            )
          })}
        </div>
      </CardContent>
    </Card>
  )
}
